/**
 * Export artwork identity: permalink-based filenames and portable metadata.
 * Reuses the existing permalink codec — no second token/URL implementation.
 */

import {
  buildSharePermalink,
  encodeConfigToPermalinkId,
} from './permalinkCodec.ts';

export const EXPORT_ARTIST_NAME = 'Valipokkann';
export const EXPORT_ARTIST_SITE = 'https://valipokkann.in';
export const EXPORT_ART_SITE = 'https://madraschecks.com';
export const EXPORT_TITLE = 'Madras Checks';
export const EXPORT_COPYRIGHT = '© Valipokkann. All rights reserved.';

export type ExportArtMetadata = {
  title: string;
  artist: string;
  artistSite: string;
  artSite: string;
  canonicalUrl: string;
  permalink: string;
  token: string;
  copyright: string;
  /** Present only when the artwork already has a canonical description. */
  description?: string;
};

/** Permalink token from the existing encodeConfigToPermalinkId; null when config cannot encode. */
export function resolveExportPermalinkToken(config: unknown): string | null {
  if (!config || typeof config !== 'object') return null;
  try {
    const token = encodeConfigToPermalinkId(config);
    return token && typeof token === 'string' ? token : null;
  } catch {
    return null;
  }
}

/**
 * Exported filename: madras-checks-{permalinkToken}-{resolution}.{ext}
 * Falls back to the legacy seed identifier only when no permalink token can be resolved.
 */
export function buildExportFilename(
  permalinkToken: string | null | undefined,
  size: number,
  fileExtension: string,
  fallbackSeed?: string | null
): string {
  const ext = String(fileExtension || 'png').replace(/^\./, '');
  const identifier =
    permalinkToken && permalinkToken.trim().length > 0
      ? permalinkToken
      : (fallbackSeed && String(fallbackSeed).trim()) || 'export';
  return `madras-checks-${identifier}-${size}.${ext}`;
}

/** Read an optional artwork description from config when one already exists. */
export function readArtworkDescription(config: unknown): string | undefined {
  if (!config || typeof config !== 'object') return undefined;
  const raw = (config as { description?: unknown }).description;
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Build export metadata for one artwork config + resolved permalink token. */
export function buildExportMetadata(
  permalinkToken: string,
  config?: unknown
): ExportArtMetadata {
  const canonicalUrl = buildSharePermalink(permalinkToken);
  const description = readArtworkDescription(config);
  const meta: ExportArtMetadata = {
    title: EXPORT_TITLE,
    artist: EXPORT_ARTIST_NAME,
    artistSite: EXPORT_ARTIST_SITE,
    artSite: EXPORT_ART_SITE,
    canonicalUrl,
    permalink: canonicalUrl,
    token: permalinkToken,
    copyright: EXPORT_COPYRIGHT,
  };
  if (description) {
    meta.description = description;
  }
  return meta;
}

/** Resolve token + metadata; returns null when permalink token cannot be derived. */
export function resolveExportIdentity(
  config: unknown
): { token: string; metadata: ExportArtMetadata } | null {
  const token = resolveExportPermalinkToken(config);
  if (!token) return null;
  return { token, metadata: buildExportMetadata(token, config) };
}

// --- PNG tEXt / iTXt embedding (pure JS, no pixel changes) ---

const PNG_SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);

function crc32Table(): Uint32Array {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
}

const CRC_TABLE = crc32Table();

function crc32(bytes: Uint8Array, initial = 0xffffffff): number {
  let c = initial;
  for (let i = 0; i < bytes.length; i++) {
    c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

function writeU32BE(view: DataView, offset: number, value: number): void {
  view.setUint32(offset, value >>> 0, false);
}

function encodeLatin1(str: string): Uint8Array {
  const out = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    out[i] = code <= 0xff ? code : 0x3f;
  }
  return out;
}

function encodeUtf8(str: string): Uint8Array {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(str);
  }
  const out: number[] = [];
  for (let i = 0; i < str.length; i++) {
    let codePoint = str.charCodeAt(i);
    if (codePoint >= 0xd800 && codePoint <= 0xdbff && i + 1 < str.length) {
      const next = str.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        codePoint = 0x10000 + ((codePoint - 0xd800) << 10) + (next - 0xdc00);
        i += 1;
      }
    }
    if (codePoint < 0x80) out.push(codePoint);
    else if (codePoint < 0x800) {
      out.push(0xc0 | (codePoint >> 6), 0x80 | (codePoint & 0x3f));
    } else if (codePoint < 0x10000) {
      out.push(
        0xe0 | (codePoint >> 12),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f)
      );
    } else {
      out.push(
        0xf0 | (codePoint >> 18),
        0x80 | ((codePoint >> 12) & 0x3f),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f)
      );
    }
  }
  return new Uint8Array(out);
}

function decodeUtf8(bytes: Uint8Array): string {
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder().decode(bytes);
  }
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    if (b < 0x80) out += String.fromCharCode(b);
    else if ((b & 0xe0) === 0xc0 && i + 1 < bytes.length) {
      out += String.fromCharCode(((b & 0x1f) << 6) | (bytes[++i] & 0x3f));
    } else if ((b & 0xf0) === 0xe0 && i + 2 < bytes.length) {
      out += String.fromCharCode(
        ((b & 0x0f) << 12) | ((bytes[++i] & 0x3f) << 6) | (bytes[++i] & 0x3f)
      );
    } else {
      out += String.fromCharCode(b);
    }
  }
  return out;
}

function concatChunks(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, p) => sum + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

/** Read blob bytes in browser and Jest/jsdom (arrayBuffer may be missing on Blob). */
export async function blobToBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') {
    return new Uint8Array(await blob.arrayBuffer());
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(reader.result));
      } else {
        reject(new Error('Failed to read blob as ArrayBuffer'));
      }
    };
    reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
    reader.readAsArrayBuffer(blob);
  });
}

function makePngTextChunk(keyword: string, text: string): Uint8Array {
  const key = encodeLatin1(keyword);
  const val = encodeLatin1(text);
  const data = new Uint8Array(key.length + 1 + val.length);
  data.set(key, 0);
  data[key.length] = 0;
  data.set(val, key.length + 1);
  return makePngChunk('tEXt', data);
}

function makePngInternationalTextChunk(keyword: string, text: string): Uint8Array {
  const key = encodeLatin1(keyword);
  const val = encodeUtf8(text);
  // keyword\0 + compression(1) + method(1) + lang\0 + translated\0 + text
  const data = new Uint8Array(key.length + 1 + 1 + 1 + 1 + 1 + val.length);
  let o = 0;
  data.set(key, o);
  o += key.length;
  data[o++] = 0; // keyword null terminator
  data[o++] = 0; // compression flag: uncompressed
  data[o++] = 0; // compression method
  data[o++] = 0; // language tag empty
  data[o++] = 0; // translated keyword empty
  data.set(val, o);
  return makePngChunk('iTXt', data);
}

function makePngChunk(type: string, data: Uint8Array): Uint8Array {
  const chunk = new Uint8Array(12 + data.length);
  const view = new DataView(chunk.buffer);
  writeU32BE(view, 0, data.length);
  for (let i = 0; i < 4; i++) {
    chunk[4 + i] = type.charCodeAt(i);
  }
  chunk.set(data, 8);
  const crcInput = chunk.subarray(4, 8 + data.length);
  writeU32BE(view, 8 + data.length, crc32(crcInput));
  return chunk;
}

function pngMetadataChunks(meta: ExportArtMetadata): Uint8Array[] {
  const chunks: Uint8Array[] = [
    makePngTextChunk('Title', meta.title),
    makePngTextChunk('Author', meta.artist),
    makePngTextChunk('Copyright', meta.copyright),
    makePngInternationalTextChunk('Artist Site', meta.artistSite),
    makePngInternationalTextChunk('Art Site', meta.artSite),
    makePngInternationalTextChunk('Canonical URL', meta.canonicalUrl),
    makePngInternationalTextChunk('Permalink', meta.permalink),
    makePngInternationalTextChunk('Token', meta.token),
  ];
  if (meta.description) {
    chunks.splice(2, 0, makePngInternationalTextChunk('Description', meta.description));
  }
  return chunks;
}

function findPngIendOffset(bytes: Uint8Array): number {
  // Walk chunks from byte 8; IEND is 12 bytes (length 0 + type + crc).
  let offset = 8;
  while (offset + 12 <= bytes.length) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const length = view.getUint32(offset, false);
    const type = String.fromCharCode(
      bytes[offset + 4],
      bytes[offset + 5],
      bytes[offset + 6],
      bytes[offset + 7]
    );
    if (type === 'IEND') {
      return offset;
    }
    offset += 12 + length;
  }
  throw new Error('PNG IEND chunk not found');
}

/** Insert metadata text chunks immediately before IEND; pixel chunks unchanged. */
export function embedPngMetadata(pngBytes: Uint8Array, meta: ExportArtMetadata): Uint8Array {
  if (pngBytes.length < 8) throw new Error('Invalid PNG: too short');
  for (let i = 0; i < 8; i++) {
    if (pngBytes[i] !== PNG_SIGNATURE[i]) {
      throw new Error('Invalid PNG signature');
    }
  }
  const iendOffset = findPngIendOffset(pngBytes);
  const prefix = pngBytes.subarray(0, iendOffset);
  const suffix = pngBytes.subarray(iendOffset);
  return concatChunks([prefix, ...pngMetadataChunks(meta), suffix]);
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildXmpPacket(meta: ExportArtMetadata): string {
  const desc = meta.description
    ? `<dc:description><rdf:Alt><rdf:li xml:lang="x-default">${escapeXml(meta.description)}</rdf:li></rdf:Alt></dc:description>`
    : '';
  return (
    '<?xpacket begin="\ufeff" id="W5M0MpCehiHzreSzNTczkc9d"?>' +
    '<x:xmpmeta xmlns:x="adobe:ns:meta/">' +
    '<rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#" ' +
    'xmlns:dc="http://purl.org/dc/elements/1.1/" ' +
    'xmlns:xmpRights="http://ns.adobe.com/xap/1.0/rights/">' +
    '<rdf:Description rdf:about="">' +
    `<dc:title><rdf:Alt><rdf:li xml:lang="x-default">${escapeXml(meta.title)}</rdf:li></rdf:Alt></dc:title>` +
    `<dc:creator><rdf:Seq><rdf:li>${escapeXml(meta.artist)}</rdf:li></rdf:Seq></dc:creator>` +
    desc +
    `<xmpRights:WebStatement>${escapeXml(meta.canonicalUrl)}</xmpRights:WebStatement>` +
    `<xmpRights:Owner><rdf:Bag><rdf:li>${escapeXml(meta.artist)}</rdf:li></rdf:Bag></xmpRights:Owner>` +
    `<dc:rights><rdf:Alt><rdf:li xml:lang="x-default">${escapeXml(meta.copyright)}</rdf:li></rdf:Alt></dc:rights>` +
    `<dc:identifier>${escapeXml(meta.token)}</dc:identifier>` +
    `<dc:source>${escapeXml(meta.artSite)}</dc:source>` +
    `<dc:relation>${escapeXml(meta.permalink)}</dc:relation>` +
    `<dc:contributor><rdf:Seq><rdf:li>${escapeXml(meta.artistSite)}</rdf:li></rdf:Seq></dc:contributor>` +
    '</rdf:Description></rdf:RDF></x:xmpmeta><?xpacket end="w"?>'
  );
}

/** Insert an APP1 XMP segment immediately after the JPEG SOI marker. */
export function embedJpegXmpMetadata(jpegBytes: Uint8Array, meta: ExportArtMetadata): Uint8Array {
  if (jpegBytes.length < 2 || jpegBytes[0] !== 0xff || jpegBytes[1] !== 0xd8) {
    throw new Error('Invalid JPEG SOI');
  }
  const xmp = encodeUtf8(buildXmpPacket(meta));
  const header = encodeUtf8('http://ns.adobe.com/xap/1.0/\0');
  const payload = new Uint8Array(header.length + xmp.length);
  payload.set(header, 0);
  payload.set(xmp, header.length);
  const segmentLength = payload.length + 2;
  const segment = new Uint8Array(4 + payload.length);
  segment[0] = 0xff;
  segment[1] = 0xe1; // APP1
  segment[2] = (segmentLength >> 8) & 0xff;
  segment[3] = segmentLength & 0xff;
  segment.set(payload, 4);
  return concatChunks([jpegBytes.subarray(0, 2), segment, jpegBytes.subarray(2)]);
}

export type MetadataEmbedResult = {
  blob: Blob;
  embedded: boolean;
  mechanism: 'png-text-chunks' | 'jpeg-xmp-app1' | 'none';
  limitation?: string;
};

/**
 * Attach artwork identity metadata when the format/pipeline supports it.
 * Never modifies pixels — only container metadata is inserted.
 */
export async function embedExportMetadata(
  blob: Blob,
  mimeType: string,
  meta: ExportArtMetadata | null | undefined
): Promise<MetadataEmbedResult> {
  if (!meta) {
    return { blob, embedded: false, mechanism: 'none', limitation: 'No permalink token available' };
  }

  const buffer = await blobToBytes(blob);

  if (mimeType === 'image/png') {
    const withMeta = embedPngMetadata(buffer, meta);
    return {
      blob: new Blob([withMeta], { type: mimeType }),
      embedded: true,
      mechanism: 'png-text-chunks',
    };
  }

  if (mimeType === 'image/jpeg') {
    const withMeta = embedJpegXmpMetadata(buffer, meta);
    return {
      blob: new Blob([withMeta], { type: mimeType }),
      embedded: true,
      mechanism: 'jpeg-xmp-app1',
    };
  }

  if (mimeType === 'image/webp') {
    return {
      blob,
      embedded: false,
      mechanism: 'none',
      limitation:
        'WebP exports from canvas.toBlob do not yet support reliable metadata embedding in this pipeline; filename and app metadata remain authoritative.',
    };
  }

  return {
    blob,
    embedded: false,
    mechanism: 'none',
    limitation: `Metadata embedding not supported for ${mimeType}`,
  };
}

/** IHDR width/height from a PNG byte stream (for tests; no decode required). */
export function readPngDimensions(pngBytes: Uint8Array): { width: number; height: number } {
  if (pngBytes.length < 24) throw new Error('PNG too short for IHDR');
  const view = new DataView(pngBytes.buffer, pngBytes.byteOffset, pngBytes.byteLength);
  const width = view.getUint32(16, false);
  const height = view.getUint32(20, false);
  return { width, height };
}
/** Parse tEXt / iTXt keyword values from a PNG byte stream (for tests). */
export function readPngTextMetadata(pngBytes: Uint8Array): Record<string, string> {
  const out: Record<string, string> = {};
  let offset = 8;
  while (offset + 12 <= pngBytes.length) {
    const view = new DataView(pngBytes.buffer, pngBytes.byteOffset, pngBytes.byteLength);
    const length = view.getUint32(offset, false);
    const type = String.fromCharCode(
      pngBytes[offset + 4],
      pngBytes[offset + 5],
      pngBytes[offset + 6],
      pngBytes[offset + 7]
    );
    const dataStart = offset + 8;
    const data = pngBytes.subarray(dataStart, dataStart + length);
    if (type === 'tEXt') {
      const nul = data.indexOf(0);
      if (nul >= 0) {
        const key = decodeUtf8(data.subarray(0, nul));
        const val = decodeUtf8(data.subarray(nul + 1));
        out[key] = val;
      }
    } else if (type === 'iTXt') {
      const nul = data.indexOf(0);
      if (nul >= 0) {
        const key = decodeUtf8(data.subarray(0, nul));
        // keyword\0comp\0method\0lang\0trans\0text
        let o = nul + 1;
        o += 2; // compression flag + method
        while (o < data.length && data[o] !== 0) o++;
        o++; // lang tag
        while (o < data.length && data[o] !== 0) o++;
        o++; // translated keyword
        out[key] = decodeUtf8(data.subarray(o));
      }
    }
    if (type === 'IEND') break;
    offset += 12 + length;
  }
  return out;
}
